import type { NoteRefDto } from "../../services/vorgangService";
import type { Kommentar } from "./kommentarModell";
import { alsKlartext } from "./kommentarModell";

/**
 * WAS EIN KOMMENTAR AUSSER TEXT NOCH TRÄGT (PO 28.09.2026): „Erwähnen und #
 * im Kommentar funktioniert nicht", „im Kommentar einen Anhang hinzufügen,
 * Bild, Dok, …".
 *
 * ══ GESCHRIEBEN WIRD KLARTEXT, GESPEICHERT BEI BEDARF HTML ═════════════════
 * Das Schreibfeld eines Kommentars bleibt ein `textarea` — ein zweites
 * `contentEditable` neben dem Notizfeld wäre ein zweiter Ort für dieselben
 * Browserfallen. Ein `#`-Bezug steht darin als `#Titel`, ein Anhang als Chip
 * UNTER dem Feld.
 *
 * Erst beim Absenden wird daraus, falls nötig, HTML: `#Titel` wird zum
 * selben Chip wie im Notizfeld (`<span data-ref-art data-ref-id>`), ein Bild
 * zum Notizbild-Marker (`<img data-bild-id>`), ein Dokument zum
 * Dokument-Chip. Ohne Bezug und ohne Anhang bleibt es Klartext — wie jeder
 * Kommentar bisher. So ändert sich für den Bestand nichts, und ein
 * `<` in „Betrag < 500" bleibt ein Zeichen.
 *
 * Beim Ändern läuft es rückwärts ({@link zerlegeKommentar}): aus dem Chip wird
 * wieder `#Titel`, aus den Anhängen die Chips unter dem Feld. Ohne diesen Weg
 * verlöre ein Kommentar beim Korrigieren eines Tippfehlers seine Anhänge.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Ein schon hochgeladener Anhang — ein `Document`. */
export type KommentarAnhang = {
    id: number;
    name: string;
    /** Bild = als Notizbild im Text gezeigt; sonst ein Dokument-Chip. */
    bild: boolean;
};

/**
 * Was zum Text dazukommt. `dateien` sind noch NICHT hochgeladen — das
 * geschieht erst beim Absenden (`baueKommentarInhalt`), damit ein
 * abgebrochener Kommentar keine verwaisten Dokumente in der Akte lässt.
 */
export type KommentarZusatz = {
    refs: NoteRefDto[];
    dateien: File[];
    anhaenge: KommentarAnhang[];
};

/** Die Klasse der Anhangzeile im gespeicherten HTML. */
export const KLASSE_ANHAENGE = "nb-kanh";

const escape = (s: string): string =>
    s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

/** `#Titel` so, wie er im Schreibfeld steht. */
export const bezugImText = (r: Pick<NoteRefDto, "label" | "id">): string =>
    `#${(r.label ?? String(r.id)).trim()}`;

/** Nur die Bezüge, deren `#Titel` noch im Text steht — gelöscht heißt gelöscht. */
export function refsImText(text: string, refs: NoteRefDto[]): NoteRefDto[] {
    const gesehen = new Set<string>();
    return refs.filter((r) => {
        const schluessel = `${r.art}:${r.id}`;
        if (gesehen.has(schluessel)) return false;
        if (!text.includes(bezugImText(r))) return false;
        gesehen.add(schluessel);
        return true;
    });
}

/** Muss dieser Kommentar als HTML gespeichert werden? */
export function brauchtHtml(text: string, zusatz?: Pick<KommentarZusatz, "refs" | "anhaenge"> | null): boolean {
    if (!zusatz) return false;
    return zusatz.anhaenge.length > 0 || refsImText(text, zusatz.refs).length > 0;
}

const chip = (art: string, id: number | string, titel: string): string =>
    `<span class="nb-ref nb-art-${escape(art)}" data-ref-art="${escape(art)}" data-ref-id="${escape(
        String(id),
    )}">${escape(titel)}</span>`;

/**
 * Klartext + Bezüge + Anhänge → HTML. Zeilenumbrüche werden `<br>`, jedes
 * `#Titel` eines gewählten Bezugs sein Chip, die Anhänge stehen in einer
 * eigenen Zeile darunter (`nb-kanh`) — daran erkennt {@link zerlegeKommentar}
 * sie wieder.
 *
 * Längere Titel zuerst ersetzen: „#Vertrag 12" darf nicht in „#Vertrag 123"
 * greifen.
 */
export function kommentarHtml(text: string, refs: NoteRefDto[], anhaenge: KommentarAnhang[]): string {
    const aktiv = refsImText(text, refs).sort(
        (a, b) => bezugImText(b).length - bezugImText(a).length,
    );
    // Platzhalter statt direkter Ersetzung: ein Chip enthält selbst Text, in
    // dem ein kürzerer Titel noch einmal gefunden würde.
    let rumpf = escape(text.trim());
    const platz: string[] = [];
    for (const r of aktiv) {
        const suche = escape(bezugImText(r));
        if (!rumpf.includes(suche)) continue;
        const marke = `\u0000${platz.length}\u0000`;
        platz.push(chip(r.art, r.id, (r.label ?? String(r.id)).trim()));
        rumpf = rumpf.split(suche).join(marke);
    }
    rumpf = rumpf.replace(/\u0000(\d+)\u0000/g, (_, i: string) => platz[Number(i)] ?? "");
    rumpf = rumpf.replace(/\r?\n/g, "<br>");

    let html = `<div>${rumpf}</div>`;
    if (anhaenge.length) {
        const teile = anhaenge.map((a) =>
            a.bild
                ? `<img data-bild-id="${a.id}" alt="${escape(a.name)}">`
                : chip("dokument", a.id, a.name),
        );
        html += `<div class="${KLASSE_ANHAENGE}">${teile.join(" ")}</div>`;
    }
    return html;
}

/**
 * Die Umkehrung für das Ändern: Text fürs Schreibfeld, die Bezüge darin und
 * die Anhänge. Braucht ein DOM (jsdom genügt) — HTML mit Regeln zu zerlegen
 * wäre an genau den Stellen falsch, an denen es darauf ankommt.
 */
export function zerlegeKommentar(k: Pick<Kommentar, "html" | "istHtml">): {
    text: string;
    refs: NoteRefDto[];
    anhaenge: KommentarAnhang[];
} {
    if (!k.istHtml) return { text: k.html, refs: [], anhaenge: [] };
    const topf = document.createElement("div");
    topf.innerHTML = k.html ?? "";

    const anhaenge: KommentarAnhang[] = [];
    for (const zeile of Array.from(topf.querySelectorAll(`.${KLASSE_ANHAENGE}`))) {
        for (const el of Array.from(zeile.querySelectorAll("img[data-bild-id],[data-ref-art]"))) {
            if (el.tagName === "IMG") {
                const id = Number(el.getAttribute("data-bild-id"));
                if (Number.isFinite(id) && id > 0) {
                    anhaenge.push({ id, name: el.getAttribute("alt") || "Bild", bild: true });
                }
            } else if (el.getAttribute("data-ref-art") === "dokument") {
                const id = Number(el.getAttribute("data-ref-id"));
                if (Number.isFinite(id) && id > 0) {
                    anhaenge.push({ id, name: (el.textContent ?? "").trim() || "Dokument", bild: false });
                }
            }
        }
        zeile.remove();
    }

    const refs: NoteRefDto[] = [];
    for (const el of Array.from(topf.querySelectorAll("[data-ref-art][data-ref-id]"))) {
        const art = el.getAttribute("data-ref-art") as NoteRefDto["art"];
        const id = el.getAttribute("data-ref-id") ?? "";
        const label = (el.textContent ?? "").trim();
        refs.push({ art, id, label });
        el.replaceWith(document.createTextNode(bezugImText({ id, label })));
    }

    const text = alsKlartext({ html: topf.innerHTML, istHtml: true });
    return { text, refs: refsImText(text, refs), anhaenge };
}

/** Wie ein Anhang hochgeladen wird — das weiß die Seite, nicht der Faden. */
export type AnhangHochladen = (datei: File) => Promise<KommentarAnhang>;

/**
 * Was beim Server ankommt. Erst hier werden die Dateien hochgeladen — ein
 * abgebrochener Kommentar hinterlässt so kein Dokument ohne Kommentar.
 */
export async function baueKommentarInhalt(
    text: string,
    zusatz: KommentarZusatz | undefined,
    hochladen: AnhangHochladen,
): Promise<{ text: string; format: "text" | "html"; refs: NoteRefDto[] }> {
    if (!zusatz) return { text, format: "text", refs: [] };
    const neu: KommentarAnhang[] = [];
    for (const datei of zusatz.dateien) neu.push(await hochladen(datei));
    const anhaenge = [...zusatz.anhaenge, ...neu];
    if (!brauchtHtml(text, { refs: zusatz.refs, anhaenge })) {
        return { text, format: "text", refs: [] };
    }
    const refs = [
        ...refsImText(text, zusatz.refs),
        ...anhaenge
            .filter((a) => !a.bild)
            .map((a): NoteRefDto => ({ art: "dokument", id: a.id, label: a.name })),
    ];
    return { text: kommentarHtml(text, zusatz.refs, anhaenge), format: "html", refs };
}
